import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkle, X, PaperPlaneRight, CircleNotch } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function Concierge() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const endRef = useRef(null);

  // Reset greeting when language changes / on first mount
  useEffect(() => {
    setMessages([{ id: `g-${Date.now()}`, role: "assistant", text: t("concierge.greeting") }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  async function send() {
    const query = q.trim();
    if (!query || busy) return;
    setMessages((m) => [...m, { id: `u-${Date.now()}-${m.length}`, role: "user", text: query }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/concierge", { query });
      setMessages((m) => [...m, { id: `a-${Date.now()}-${m.length}`, role: "assistant", text: data.reply || t("concierge.noReply") }]);
    } catch (e) {
      setMessages((m) => [...m, { id: `e-${Date.now()}-${m.length}`, role: "assistant", text: t("concierge.unavailable") }]);
    } finally { setBusy(false); }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="concierge-fab"
        className="fixed bottom-6 right-6 rtl:right-auto rtl:left-6 z-50 btn-primary shadow-lg hover:scale-105 transition-transform"
        style={{ borderRadius: 9999, padding: ".85rem 1.2rem" }}
      >
        <Sparkle size={18} weight="duotone" /> {t("concierge.ask")}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm flex items-end md:items-center justify-center md:p-8"
            onClick={() => setOpen(false)}
            data-testid="concierge-panel"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="w-full md:w-[560px] h-[80vh] md:h-[640px] bg-white dark:bg-zinc-900 border-2 border-black dark:border-zinc-100 flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-800">
                <div className="flex items-center gap-2">
                  <Sparkle size={18} weight="duotone" className="text-blue-600 dark:text-blue-400" />
                  <div className="font-display font-bold text-lg text-zinc-950 dark:text-zinc-100">{t("concierge.title")}</div>
                </div>
                <button onClick={() => setOpen(false)} data-testid="concierge-close" className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300"><X size={18} /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {messages.map((m) => (
                  <div key={m.id} className={m.role === "user" ? "ms-auto max-w-[80%]" : "me-auto max-w-[90%]"}>
                    <div className={(m.role === "user" ? "bg-zinc-950 dark:bg-zinc-100 text-white dark:text-zinc-950" : "bg-blue-50 dark:bg-blue-950/40 text-zinc-900 dark:text-zinc-100 border-s-2 border-blue-600 dark:border-blue-400") + " px-4 py-3 text-sm leading-relaxed whitespace-pre-line"}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="me-auto text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> {t("concierge.thinking")}</div>
                )}
                <div ref={endRef} />
              </div>

              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-zinc-200 dark:border-zinc-800 p-3 flex gap-2">
                <input
                  data-testid="concierge-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("concierge.placeholder")}
                  className="input-box flex-1"
                />
                <button data-testid="concierge-send" type="submit" disabled={busy || !q.trim()} className="btn-accent">
                  <PaperPlaneRight size={16} className="rtl-mirror" />
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
